import Fuse from "fuse.js";

// Digits identify a specific, discrete value (a bin range, a year, a count),
// where "close" doesn't mean "same" the way it does for a misspelled word — a
// fuzzy score loose enough to catch letter typos ("Northrn" -> "NORTHERN") is
// also loose enough to match "1000-2000" against "2000-3000" or "4 Year"
// against "5 Year" (both a single edit inside a short string), which is loss
// of precision, not typo tolerance. Digits must therefore match exactly,
// entirely outside of Fuse's scoring; only the surrounding letters get fuzzy
// tolerance.
//
// Run-exact (not substring): a query digit run must appear as one of value's
// own digit runs verbatim. "2024" does NOT satisfy a value whose only digit
// run is "20242" — that's a different number, not the same one with extra
// digits nearby.
const extractDigitRuns = (value: string): string[] => value.match(/\d+/g) ?? [];

export function digitsAreConsistent(value: string, query: string): boolean {
  const queryDigitRuns = extractDigitRuns(query);
  if (queryDigitRuns.length === 0) return true;

  const valueDigitRuns = new Set(extractDigitRuns(value));
  return queryDigitRuns.every((run) => valueDigitRuns.has(run));
}

// Damerau-Levenshtein (adjacent transposition costs 1 edit, not 2) — Fuse's
// own scoring is plain-edit-distance-based, so it never resolves a
// transposition ("Premian" -> "Permian") as cheaply as a human typo actually
// is. Used below as a direct whole-string check (not a substring search)
// specifically for the case Fuse's ratio can't reliably cover: a short-to-
// medium VALUE typed with a transposition/drop/substitution, where the
// query is close to the value's full length rather than a fragment of it.
function damerauLevenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }

  return d[m][n];
}

// Absolute edit budget, tiered by query length rather than a ratio — a ratio
// makes tolerance a function of how much the user has typed rather than how
// severe the typo is (see review: "Bell"/"well" matches at 4 chars while
// "Permian"/"Premian" needed 7 to clear the same ratio). Below 4 characters
// there's too little information to safely tolerate any edit; 4-8 tolerates
// one (a single transposition/drop/substitution); above that, two.
function maxEditsFor(length: number): number {
  if (length < 4) return 0;
  if (length <= 8) return 1;
  return 2;
}

// Ceiling on the whole-string Damerau comparison below (well above real
// label lengths of 40-60 chars, but far under MAX_FUZZY_QUERY_LENGTH) — bounds
// its O(a.length * b.length) cost to a fixed, cheap constant regardless of how
// long a pasted query gets, since maxEditsFor alone doesn't cap query length.
const MAX_DAMERAU_COMPARE_LENGTH = 64;

// Backstop against a pathological paste, not a behavioral boundary: real
// group-by column titles run 40-60 chars and DO get typo'd, so this sits well
// above any realistic label length.
export const MAX_FUZZY_QUERY_LENGTH = 256;

// ignoreLocation + a large distance so a typo'd query can match anywhere
// inside value, not just near the start (Fuse's default scoring otherwise
// penalizes matches by how far they sit from position 0 — the wrong model
// when `value` is a full label and `query` is typed against any part of it).
// threshold picked empirically against realistic LETTER typos —
// transpositions, drops, substitutions, e.g. "Premian" -> "Permian" (2
// substitutions / 7 chars = 0.286), "Baken" -> "Bakken", "Northrn" ->
// "NORTHERN" — all match at this setting; unrelated strings score well above
// it.
// Untyped (rather than IFuseOptions<string>) so it can be spread into a
// `new Fuse<T>` call for any document type `T`, e.g. filterByText.ts's
// `new Fuse<IGroupByListColumn>` — none of these options are keyed to the
// document type.
export const FUZZY_MATCH_OPTIONS = {
  ignoreLocation: true,
  distance: 100000,
  threshold: 0.3,
  minMatchCharLength: 1
};

/**
 * Whether `query` fuzzy-matches anywhere inside `value`.
 *
 * Exact substring is checked first (the previous, exact-match behavior, so it
 * never regresses — this also makes an empty `query` match everything,
 * without a separate branch for it). Digits are then required to match
 * exactly regardless of what follows (see `digitsAreConsistent`). Two fuzzy
 * strategies follow, cheapest/most-precise first:
 *
 * 1. A direct Damerau-Levenshtein comparison against a length-tiered
 *    absolute edit budget, gated to whole-string-length pairs — this is what
 *    resolves a transposition ("Premian" -> "Permian") that Fuse's own
 *    plain-edit-distance scoring can't cheaply reach.
 * 2. `fuse.js` — already a project dependency, see geoAttributeMatcher.ts and
 *    ArpsUploadWizardHelpers.tsx — for the general case: a typo'd query
 *    found somewhere inside a longer value.
 */
export function isFuzzyMatch(value: string, query: string): boolean {
  const v = value.toLowerCase();
  const q = query.toLowerCase();

  if (v.includes(q)) {
    return true;
  }

  if (q.length > MAX_FUZZY_QUERY_LENGTH || !digitsAreConsistent(value, query)) {
    return false;
  }

  const editBudget = maxEditsFor(q.length);
  if (
    q.length <= MAX_DAMERAU_COMPARE_LENGTH &&
    Math.abs(v.length - q.length) <= editBudget &&
    damerauLevenshteinDistance(v, q) <= editBudget
  ) {
    return true;
  }

  return new Fuse([v], FUZZY_MATCH_OPTIONS).search(q).length > 0;
}
